import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import {
  useCreateEntityFromMentionOptional,
  type CreateEntityRequest,
} from '../contexts/CreateEntityFromMentionContext';
import { API_BASE } from '../config';
import { dispatchMentionableEntitiesChanged } from '../utils/mentionable-entities-events';
import { CharacterDetail } from './CharacterDetail';
import { AddLocationModal } from './AddLocationModal';
import { AddGenericResourceModal } from './AddGenericResourceModal';
import { AddTimelineEventModal } from './AddTimelineEventModal';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { RichTextField, type MentionableEntity } from './RichTextField';

function useMasterKey(): string | null {
  const [masterKey] = useState(() => new URLSearchParams(window.location.search).get('masterKey'));
  return masterKey;
}

function authHeaders(token: string | null, masterKey: string | null, json = false): HeadersInit {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function finishMentionCreate(
  mentionCreate: ReturnType<typeof useCreateEntityFromMentionOptional>,
  request: CreateEntityRequest,
  entity: MentionableEntity,
): void {
  mentionCreate?.rememberCreatedEntity(entity);
  request.onCreated?.(entity);
  if (request.projectId) {
    dispatchMentionableEntitiesChanged(request.projectId);
  }
  mentionCreate?.closeCreateEntity();
}

function withMasterKey(url: string, masterKey: string | null): string {
  if (!masterKey) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}masterKey=${encodeURIComponent(masterKey)}`;
}

interface CreateNamedEntityDialogProps {
  open: boolean;
  title: string;
  nameLabel: string;
  namePlaceholder: string;
  name: string;
  onNameChange: (value: string) => void;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  description?: string;
  onDescriptionChange?: (value: string) => void;
  extraFields?: React.ReactNode;
  saving: boolean;
  saveDisabled: boolean;
  error?: string | null;
  onSave: () => void;
  onClose: () => void;
}

const CreateNamedEntityDialog: React.FC<CreateNamedEntityDialogProps> = ({
  open,
  title,
  nameLabel,
  namePlaceholder,
  name,
  onNameChange,
  descriptionLabel,
  descriptionPlaceholder,
  description,
  onDescriptionChange,
  extraFields,
  saving,
  saveDisabled,
  error,
  onSave,
  onClose,
}) => {
  const { t } = useLocale();
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !saving && onClose()}>
      <DialogContent
        className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}
        data-create-entity-from-mention=""
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <EntityDetailModalLayout
          header={<EntityDetailModalTitle>{title}</EntityDetailModalTitle>}
          footer={
            <DialogFooter className="gap-2 sm:justify-end">
              <CancelButton onClick={onClose} />
              <Button
                dialogPrimary
                className="inline-flex items-center gap-1.5"
                onClick={onSave}
                disabled={saveDisabled || saving}
              >
                {saving ? (
                  t('adding')
                ) : (
                  <>
                    {t('add')}
                    <DialogPrimaryShortcut className="ml-0.5" />
                  </>
                )}
              </Button>
            </DialogFooter>
          }
        >
          <div className="space-y-4 p-4">
            {extraFields}
            <div>
              <Label className="mb-2 block">{nameLabel} *</Label>
              <Input
                type="text"
                value={name}
                onChange={(event) => onNameChange(event.target.value)}
                placeholder={namePlaceholder}
                disabled={saving}
                autoFocus={!extraFields}
              />
            </div>
            {onDescriptionChange && descriptionLabel && (
              <div>
                <Label className="mb-2 block">{descriptionLabel}</Label>
                <RichTextField
                  value={description ?? ''}
                  onChange={onDescriptionChange}
                  placeholder={descriptionPlaceholder}
                  minHeight="150px"
                />
              </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </EntityDetailModalLayout>
      </DialogContent>
    </Dialog>
  );
};

const ChapterCreateDialog: React.FC<{
  request: CreateEntityRequest;
  onClose: () => void;
  onCreated: (entity: MentionableEntity) => void;
}> = ({ request, onClose, onCreated }) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showErrorToast } = useToast();
  const masterKey = useMasterKey();
  const [title, setTitle] = useState(request.name);
  const [order, setOrder] = useState('1');
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(request.name);
    setSummary('');
    setError(null);
    const documentId = request.documentId;
    if (!documentId) {
      setOrder('1');
      return;
    }
    const loadNextOrder = async (): Promise<void> => {
      try {
        const response = await fetch(
          withMasterKey(`${API_BASE}/api/documents/${documentId}/chapters`, masterKey),
          { headers: authHeaders(token, masterKey) },
        );
        if (!response.ok) return;
        const data = await response.json();
        const allChapters = Object.values(data.chaptersByLevel || {}).flat() as Array<{
          isManual?: boolean;
          order?: number;
        }>;
        const manualChapters = allChapters.filter((chapter) => chapter.isManual);
        const maxOrder =
          manualChapters.length > 0
            ? Math.max(...manualChapters.map((chapter) => chapter.order || 0))
            : 0;
        setOrder(String(maxOrder + 1));
      } catch {
        setOrder('1');
      }
    };
    void loadNextOrder();
  }, [request, masterKey, token]);

  return (
    <CreateNamedEntityDialog
      open
      title={t('add_chapter')}
      nameLabel={t('title')}
      namePlaceholder={t('chapter_title_placeholder')}
      name={title}
      onNameChange={setTitle}
      descriptionLabel={t('summary')}
      descriptionPlaceholder={t('chapter_summary_placeholder')}
      description={summary}
      onDescriptionChange={setSummary}
      extraFields={
        <div className="w-24">
          <Label className="mb-2 block">{t('chapter_order')} *</Label>
          <Input
            type="number"
            min="1"
            value={order}
            onChange={(event) => setOrder(event.target.value)}
            placeholder={t('chapter_order_placeholder')}
            disabled={saving}
            autoFocus
          />
        </div>
      }
      saving={saving}
      saveDisabled={!title.trim() || !order.trim()}
      error={error}
      onClose={onClose}
      onSave={async () => {
        const orderNum = parseInt(order.trim(), 10);
        if (!title.trim() || !request.documentId || Number.isNaN(orderNum) || orderNum <= 0) return;
        try {
          setSaving(true);
          setError(null);
          const response = await fetch(
            withMasterKey(`${API_BASE}/api/documents/${request.documentId}/chapters`, masterKey),
            {
              method: 'POST',
              headers: authHeaders(token, masterKey, true),
              body: JSON.stringify({
                order: orderNum,
                title: title.trim(),
                summary: summary.trim() || undefined,
              }),
            },
          );
          if (!response.ok) throw new Error('Failed to create chapter');
          const data = await response.json();
          const chapter = data.chapter as { id: string; title: string; order?: number };
          onCreated({
            id: chapter.id,
            label: chapter.title,
            type: 'manual_chapter',
            documentId: request.documentId,
            order: chapter.order,
          });
        } catch (err) {
          console.error('Error creating chapter:', err);
          const message = t('failed_to_create_chapter');
          setError(message);
          showErrorToast(message);
        } finally {
          setSaving(false);
        }
      }}
    />
  );
};

const StorylineCreateDialog: React.FC<{
  request: CreateEntityRequest;
  onClose: () => void;
  onCreated: (entity: MentionableEntity) => void;
}> = ({ request, onClose, onCreated }) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showErrorToast } = useToast();
  const masterKey = useMasterKey();
  const [title, setTitle] = useState(request.name);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(request.name);
    setDescription('');
    setError(null);
  }, [request]);

  return (
    <CreateNamedEntityDialog
      open
      title={t('add_storyline')}
      nameLabel={t('title')}
      namePlaceholder={t('storyline_title_placeholder')}
      name={title}
      onNameChange={setTitle}
      descriptionLabel={t('description')}
      descriptionPlaceholder={t('storyline_description_placeholder')}
      description={description}
      onDescriptionChange={setDescription}
      saving={saving}
      saveDisabled={!title.trim()}
      error={error}
      onClose={onClose}
      onSave={async () => {
        if (!title.trim() || !request.documentId) return;
        try {
          setSaving(true);
          setError(null);
          const response = await fetch(
            withMasterKey(`${API_BASE}/api/documents/${request.documentId}/storylines`, masterKey),
            {
              method: 'POST',
              headers: authHeaders(token, masterKey, true),
              body: JSON.stringify({
                title: title.trim(),
                description: description.trim() || undefined,
              }),
            },
          );
          if (!response.ok) throw new Error('Failed to create storyline');
          const data = await response.json();
          const storyline = data.storyline as { id: string; title: string };
          onCreated({
            id: storyline.id,
            label: storyline.title,
            type: 'storyline',
            documentId: request.documentId,
          });
        } catch (err) {
          console.error('Error creating storyline:', err);
          const message = t('failed_to_create_storyline');
          setError(message);
          showErrorToast(message);
        } finally {
          setSaving(false);
        }
      }}
    />
  );
};

const SceneCreateDialog: React.FC<{
  request: CreateEntityRequest;
  onClose: () => void;
  onCreated: (entity: MentionableEntity) => void;
}> = ({ request, onClose, onCreated }) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showErrorToast } = useToast();
  const masterKey = useMasterKey();
  const [title, setTitle] = useState(request.name);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState(1);

  useEffect(() => {
    setTitle(request.name);
    setDescription('');
    setError(null);
    const documentId = request.documentId;
    if (!documentId) {
      setOrder(1);
      return;
    }
    const loadNextOrder = async (): Promise<void> => {
      try {
        const response = await fetch(
          withMasterKey(`${API_BASE}/api/documents/${documentId}/scenes`, masterKey),
          { headers: authHeaders(token, masterKey) },
        );
        if (!response.ok) return;
        const data = await response.json();
        const scenes = Array.isArray(data.scenes) ? data.scenes : [];
        const maxOrder =
          scenes.length > 0 ? Math.max(...scenes.map((scene: { order?: number }) => scene.order || 0)) : 0;
        setOrder(maxOrder + 1);
      } catch {
        setOrder(1);
      }
    };
    void loadNextOrder();
  }, [request, masterKey, token]);

  return (
    <CreateNamedEntityDialog
      open
      title={t('add_scene')}
      nameLabel={t('title')}
      namePlaceholder={t('scene_title_placeholder')}
      name={title}
      onNameChange={setTitle}
      descriptionLabel={t('description')}
      descriptionPlaceholder={t('scene_description_placeholder')}
      description={description}
      onDescriptionChange={setDescription}
      saving={saving}
      saveDisabled={!title.trim()}
      error={error}
      onClose={onClose}
      onSave={async () => {
        if (!title.trim() || !request.documentId) return;
        try {
          setSaving(true);
          setError(null);
          const response = await fetch(
            withMasterKey(`${API_BASE}/api/documents/${request.documentId}/scenes`, masterKey),
            {
              method: 'POST',
              headers: authHeaders(token, masterKey, true),
              body: JSON.stringify({
                order,
                title: title.trim(),
                description: description.trim() || undefined,
              }),
            },
          );
          if (!response.ok) throw new Error('Failed to create scene');
          const data = await response.json();
          const scene = data.scene as { id: string; title: string; order?: number };
          onCreated({
            id: scene.id,
            label: scene.title,
            type: 'scene',
            documentId: request.documentId,
            order: scene.order,
          });
        } catch (err) {
          console.error('Error creating scene:', err);
          const message = t('failed_to_create_scene');
          setError(message);
          showErrorToast(message);
        } finally {
          setSaving(false);
        }
      }}
    />
  );
};

const DocumentCreateDialog: React.FC<{
  request: CreateEntityRequest;
  onClose: () => void;
  onCreated: (entity: MentionableEntity) => void;
}> = ({ request, onClose, onCreated }) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showErrorToast } = useToast();
  const [title, setTitle] = useState(request.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(request.name);
    setError(null);
  }, [request]);

  return (
    <CreateNamedEntityDialog
      open
      title={t('new document')}
      nameLabel={t('title')}
      namePlaceholder={t('untitled_document')}
      name={title}
      onNameChange={setTitle}
      saving={saving}
      saveDisabled={!title.trim()}
      error={error}
      onClose={onClose}
      onSave={async () => {
        if (!title.trim() || !request.projectId || !token) return;
        try {
          setSaving(true);
          setError(null);
          const response = await fetch(`${API_BASE}/api/projects/${request.projectId}/documents`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ title: title.trim() }),
          });
          if (!response.ok) throw new Error('Failed to create document');
          const data = await response.json();
          window.dispatchEvent(
            new CustomEvent('document-created', {
              detail: { projectId: request.projectId, documentId: data.id, title: data.title },
            }),
          );
          onCreated({
            id: data.id,
            label: data.title ?? title.trim(),
            type: 'document',
            documentId: data.id,
          });
        } catch (err) {
          console.error('Error creating document:', err);
          const message = t('failed_to_create_document');
          setError(message);
          showErrorToast(message);
        } finally {
          setSaving(false);
        }
      }}
    />
  );
};

export const CreateEntityFromMentionHost: React.FC = () => {
  const mentionCreate = useCreateEntityFromMentionOptional();
  const request = mentionCreate?.request ?? null;
  const onClose = mentionCreate?.closeCreateEntity ?? (() => {});
  const onCreated = (entity: MentionableEntity): void => {
    if (!request) return;
    finishMentionCreate(mentionCreate, request, entity);
  };

  if (!request) return null;

  if (request.type === 'character' && request.documentId) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}
          data-create-entity-from-mention=""
        >
          <DialogTitle className="sr-only">{request.name}</DialogTitle>
          <CharacterDetail
            documentId={request.documentId}
            projectId={request.projectId}
            characterId={null}
            isModal
            initialName={request.name}
            onClose={onClose}
            onCreate={(character) => {
              onCreated({
                id: character.id,
                label: character.name,
                type: 'character',
                documentId: request.documentId,
              });
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (request.type === 'location' && request.documentId) {
    return (
      <AddLocationModal
        open
        onOpenChange={(open) => !open && onClose()}
        documentId={request.documentId}
        projectId={request.projectId}
        initialName={request.name}
        onCreated={(location) => {
          onCreated({
            id: location.id,
            label: location.name,
            type: 'location',
            documentId: request.documentId,
          });
        }}
      />
    );
  }

  if (request.type === 'manual_chapter' && request.documentId) {
    return <ChapterCreateDialog request={request} onClose={onClose} onCreated={onCreated} />;
  }

  if (request.type === 'storyline' && request.documentId) {
    return <StorylineCreateDialog request={request} onClose={onClose} onCreated={onCreated} />;
  }

  if (request.type === 'scene' && request.documentId) {
    return <SceneCreateDialog request={request} onClose={onClose} onCreated={onCreated} />;
  }

  if (request.type === 'resource' && request.projectId) {
    return (
      <AddGenericResourceModal
        isOpen
        onClose={onClose}
        projectId={request.projectId}
        initialTitle={request.name}
        onCreated={(resource) => {
          onCreated({
            id: resource.id,
            label: resource.title,
            type: 'resource',
          });
        }}
      />
    );
  }

  if (request.type === 'timeline_item' && request.projectId) {
    const now = new Date();
    return (
      <AddTimelineEventModal
        isOpen
        onClose={onClose}
        projectId={request.projectId}
        onSuccess={(created) => {
          if (!created?.id) {
            dispatchMentionableEntitiesChanged(request.projectId);
            return;
          }
          onCreated({
            id: created.id,
            label: created.name,
            type: 'timeline_item',
          });
        }}
        defaultsForNewItem={{
          name: request.name,
          item_type: 'other',
          tags: [],
          start_date: now.toISOString(),
          start_precision: 'day',
          end_date: null,
          end_precision: null,
        }}
      />
    );
  }

  if (request.type === 'document' && request.projectId) {
    return <DocumentCreateDialog request={request} onClose={onClose} onCreated={onCreated} />;
  }

  return null;
};
